import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { Analytics as VercelAnalytics } from '@vercel/analytics/react';
import { SpeedInsights } from '@vercel/speed-insights/react';
import { Toaster } from '@ui/toaster';
import { LoadingProvider } from '@shared/contexts/LoadingContext';
import ErrorBoundaryRoute from '@shared/components/ErrorBoundaryRoute';
import ProtectedRoute from '@features/auth/components/ProtectedRoute';
import LoadingSpinner from '@shared/components/LoadingSpinner';

/*
 * Route Map
 *
 * Public routes:
 * - /
 * - /login
 * - /menu
 * - /request-quote
 *
 * Admin routes:
 * - /dashboard
 * - /analytics
 * - /google-reviews
 * - /catering/calendar
 * - /product-intelligence
 * - /clients
 * - /inventory
 * - /inventory/daily-entry
 * - /inventory/list
 * - /inventory/alerts
 * - /inventory/reports
 * - /inventory/import
 * - /invoices
 * - /menu-builder
 * - /menu-category
 * - /recipe-cost
 * - /proposal-builder
 * - /proposals
 * - /food-tasting
 * - /food-tasting/new
 * - /food-tasting/:eventId
 * - /food-tasting/:eventId/review
 * - /buffet-labels
 * - /document-test
 * - /live-editor-test
 * - /apryse-editor
 */

// Lazy load Dashboard (used on multiple routes)
const Dashboard = lazy(() => import('@shared/components/Dashboard'));

// Lazy load pages for code splitting - reduces initial bundle size
const Analytics = lazy(() => import('@features/analytics/pages/Analytics'));
const CeoProductIntelligencePage = lazy(() => import('@features/analytics/pages/CeoProductIntelligencePage'));
const GoogleReviewsPage = lazy(() => import('@features/google-review/pages/GoogleReviewsPage'));
const Clients = lazy(() => import('@features/clients/pages/Clients'));
const DashboardPage = lazy(() => import('@features/analytics/pages/DashboardPage'));
const InventoryLayout = lazy(() => import('@features/inventory/components/InventoryLayout'));
const InventoryDashboard = lazy(() => import('@features/inventory/pages/InventoryDashboard'));
const DailyStockEntry = lazy(() => import('@features/inventory/pages/DailyStockEntry'));
const InventoryList = lazy(() => import('@features/inventory/pages/InventoryList'));
const LowStockAlerts = lazy(() => import('@features/inventory/pages/LowStockAlerts'));
const InventoryReports = lazy(() => import('@features/inventory/pages/InventoryReports'));
const ExcelImport = lazy(() => import('@features/inventory/pages/ExcelImport'));
const Invoices = lazy(() => import('@features/invoices/pages/Invoices'));
const MenuBuilder = lazy(() => import('@features/menu/pages/MenuBuilder'));
const MenuCategory = lazy(() => import('@features/menu/pages/MenuCategory'));
const ProposalBuilder = lazy(() => import('@features/proposal/pages/ProposalBuilder'));
const ProposalsList = lazy(() => import('@features/proposal/pages/ProposalsList'));
const RecipeCostManagement = lazy(() => import('@features/menu/pages/RecipeCostManagement'));
const CustomerProposalBuilder = lazy(() => import('@features/proposal/pages/CustomerProposalBuilder'));
const CustomerSmartFormLanding = lazy(() => import('@features/customer-portal/pages/CustomerSmartFormLanding'));
const PortalCustomerSmartForm = lazy(() => import('@features/customer-portal/pages/PortalCustomerSmartForm'));
const CustomerMenuView = lazy(() => import('@features/menu/pages/CustomerMenuView'));
const PortalFlowLayout = lazy(() => import('@features/customer-portal/components/proposal/PortalFlowLayout'));
const Login = lazy(() => import('@features/auth/pages/Login'));
const DocumentTestPage = lazy(() => import('@features/document/pages/DocumentTestPage'));
const LiveDocumentEditorTestPage = lazy(() => import('@features/document/pages/LiveDocumentEditorTestPage'));
const ApryseDocumentEditorPage = lazy(() => import('@features/document/pages/ApryseDocumentEditorPage'));
const NotFound = lazy(() => import('./pages/NotFound'));
const Forbidden = lazy(() => import('@features/auth/pages/Forbidden'));

// Restaurant/Catering RBAC examples
const RestaurantOrders = lazy(() => import('@features/restaurant/pages/RestaurantOrders'));
const CateringEvents = lazy(() => import('@features/catering/pages/CateringEvents'));
const CateringCalendarPage = lazy(() => import('@features/catering/pages/CateringCalendarPage'));
const TastingListPage = lazy(() => import('@features/food-tasting/pages/TastingListPage'));
const TastingNewEventPage = lazy(() => import('@features/food-tasting/pages/TastingNewEventPage'));
const TastingCapturePage = lazy(() => import('@features/food-tasting/pages/TastingCapturePage'));
const TastingReviewPage = lazy(() => import('@features/food-tasting/pages/TastingReviewPage'));
const BuffetLabelPrintPage = lazy(() => import('@features/buffet-labels/pages/BuffetLabelPrintPage'));

// Admin RBAC management pages
const UsersAdmin = lazy(() => import('@features/auth/pages/admin/UsersAdmin'));
const RolesAdmin = lazy(() => import('@features/auth/pages/admin/RolesAdmin'));
const PermissionsAdmin = lazy(() => import('@features/auth/pages/admin/PermissionsAdmin'));

// Loading fallback component
const PageLoader = () => (
  <div className="flex min-h-screen items-center justify-center bg-background">
    <LoadingSpinner />
  </div>
);

function AppRoutes() {
  return (
    <LoadingProvider>
      <div className="min-h-screen bg-background">
        <Suspense fallback={<PageLoader />}>
          <Routes>
          <Route path="/" element={
            <ErrorBoundaryRoute>
            <Suspense fallback={<PageLoader />}>
              <Login />
            </Suspense>
          </ErrorBoundaryRoute>
          } />
          <Route path="/request-quote" element={
            <ErrorBoundaryRoute>
              <Suspense fallback={<PageLoader />}>
                <CustomerSmartFormLanding />
              </Suspense>
            </ErrorBoundaryRoute>
          } />
          <Route path="/login" element={
            <ErrorBoundaryRoute>
              <Suspense fallback={<PageLoader />}>
                <Login />
              </Suspense>
            </ErrorBoundaryRoute>
          } />
          <Route path="/forbidden" element={
            <ErrorBoundaryRoute>
              <Suspense fallback={<PageLoader />}>
                <Forbidden />
              </Suspense>
            </ErrorBoundaryRoute>
          } />
        
          {/* Customer-Facing Routes (Public) */}
          <Route path="/menu" element={
            <ErrorBoundaryRoute>
              <Suspense fallback={<PageLoader />}>
                <CustomerMenuView />
              </Suspense>
            </ErrorBoundaryRoute>
          } />
          <Route path="/request-quote" element={
            <ErrorBoundaryRoute>
              <Suspense fallback={<PageLoader />}>
                <CustomerProposalBuilder />
              </Suspense>
            </ErrorBoundaryRoute>
          } />
          <Route path="/request-quote/smart" element={
            <ErrorBoundaryRoute>
              <Suspense fallback={<PageLoader />}>
                <CustomerSmartFormLanding />
              </Suspense>
            </ErrorBoundaryRoute>
          } />
          <Route path="/request-quote/builder" element={
            <ErrorBoundaryRoute>
              <Suspense fallback={<PageLoader />}>
                <CustomerProposalBuilder />
              </Suspense>
            </ErrorBoundaryRoute>
          } />
          <Route path="/portal" element={
            <ErrorBoundaryRoute>
              <Suspense fallback={<PageLoader />}>
                <PortalFlowLayout />
              </Suspense>
            </ErrorBoundaryRoute>
          } />
          <Route path="/portal-customer" element={
            <ErrorBoundaryRoute>
              <Suspense fallback={<PageLoader />}>
                <PortalCustomerSmartForm />
              </Suspense>
            </ErrorBoundaryRoute>
          } />
          <Route path="/portal-customer/smart-form" element={
            <ErrorBoundaryRoute>
              <Suspense fallback={<PageLoader />}>
                <PortalCustomerSmartForm />
              </Suspense>
            </ErrorBoundaryRoute>
          } />
          
          {/* Admin Routes (Protected) */}
          <Route element={<ProtectedRoute />}>
            <Route path="/dashboard" element={
              <ErrorBoundaryRoute>
                <Suspense fallback={<PageLoader />}>
                  <Dashboard>
                    <Suspense fallback={<PageLoader />}>
                      <DashboardPage />
                    </Suspense>
                  </Dashboard>
                </Suspense>
              </ErrorBoundaryRoute>
            } />
            <Route path="/analytics" element={
              <ErrorBoundaryRoute>
                <Suspense fallback={<PageLoader />}>
                  <Dashboard>
                    <Suspense fallback={<PageLoader />}>
                      <Analytics />
                    </Suspense>
                  </Dashboard>
                </Suspense>
              </ErrorBoundaryRoute>
            } />
            <Route path="/google-reviews" element={
              <ErrorBoundaryRoute>
                <Suspense fallback={<PageLoader />}>
                  <Dashboard>
                    <Suspense fallback={<PageLoader />}>
                      <GoogleReviewsPage />
                    </Suspense>
                  </Dashboard>
                </Suspense>
              </ErrorBoundaryRoute>
            } />
            <Route path="/product-intelligence" element={
              <ErrorBoundaryRoute>
                <Suspense fallback={<PageLoader />}>
                  <Dashboard>
                    <Suspense fallback={<PageLoader />}>
                      <CeoProductIntelligencePage />
                    </Suspense>
                  </Dashboard>
                </Suspense>
              </ErrorBoundaryRoute>
            } />
            <Route path="/clients" element={
              <ErrorBoundaryRoute>
                <Suspense fallback={<PageLoader />}>
                  <Dashboard>
                    <Suspense fallback={<PageLoader />}>
                      <Clients />
                    </Suspense>
                  </Dashboard>
                </Suspense>
              </ErrorBoundaryRoute>
            } />
            <Route path="/inventory" element={
              <ErrorBoundaryRoute>
                <Suspense fallback={<PageLoader />}>
                  <Dashboard>
                    <Suspense fallback={<PageLoader />}>
                      <InventoryLayout />
                    </Suspense>
                  </Dashboard>
                </Suspense>
              </ErrorBoundaryRoute>
            }>
              <Route index element={<Suspense fallback={<PageLoader />}><InventoryDashboard /></Suspense>} />
              <Route path="daily-entry" element={<Suspense fallback={<PageLoader />}><DailyStockEntry /></Suspense>} />
              <Route path="list" element={<Suspense fallback={<PageLoader />}><InventoryList /></Suspense>} />
              <Route path="alerts" element={<Suspense fallback={<PageLoader />}><LowStockAlerts /></Suspense>} />
              <Route path="reports" element={<Suspense fallback={<PageLoader />}><InventoryReports /></Suspense>} />
              <Route path="import" element={<Suspense fallback={<PageLoader />}><ExcelImport /></Suspense>} />
            </Route>
            <Route path="/invoices" element={
              <ErrorBoundaryRoute>
                <Suspense fallback={<PageLoader />}>
                  <Dashboard>
                    <Suspense fallback={<PageLoader />}>
                      <Invoices />
                    </Suspense>
                  </Dashboard>
                </Suspense>
              </ErrorBoundaryRoute>
            } />
            <Route path="/menu-builder" element={
              <ErrorBoundaryRoute>
                <Suspense fallback={<PageLoader />}>
                  <Dashboard>
                    <Suspense fallback={<PageLoader />}>
                      <MenuBuilder />
                    </Suspense>
                  </Dashboard>
                </Suspense>
              </ErrorBoundaryRoute>
            } />
            <Route path="/menu-category" element={
              <ErrorBoundaryRoute>
                <Suspense fallback={<PageLoader />}>
                  <Dashboard>
                    <Suspense fallback={<PageLoader />}>
                      <MenuCategory />
                    </Suspense>
                  </Dashboard>
                </Suspense>
              </ErrorBoundaryRoute>
            } />
            <Route path="/recipe-cost" element={
              <ErrorBoundaryRoute>
                <Suspense fallback={<PageLoader />}>
                  <Dashboard>
                    <Suspense fallback={<PageLoader />}>
                      <RecipeCostManagement />
                    </Suspense>
                  </Dashboard>
                </Suspense>
              </ErrorBoundaryRoute>
            } />
            <Route path="/restaurant/orders" element={
              <ErrorBoundaryRoute>
                <Suspense fallback={<PageLoader />}>
                  <Dashboard>
                    <Suspense fallback={<PageLoader />}>
                      <RestaurantOrders />
                    </Suspense>
                  </Dashboard>
                </Suspense>
              </ErrorBoundaryRoute>
            } />
            <Route path="/catering/calendar" element={
              <ErrorBoundaryRoute>
                <Suspense fallback={<PageLoader />}>
                  <Dashboard>
                    <Suspense fallback={<PageLoader />}>
                      <CateringCalendarPage />
                    </Suspense>
                  </Dashboard>
                </Suspense>
              </ErrorBoundaryRoute>
            } />
            <Route path="/catering/events" element={
              <ErrorBoundaryRoute>
                <Suspense fallback={<PageLoader />}>
                  <Dashboard>
                    <Suspense fallback={<PageLoader />}>
                      <CateringEvents />
                    </Suspense>
                  </Dashboard>
                </Suspense>
              </ErrorBoundaryRoute>
            } />
            <Route path="/food-tasting" element={
              <ErrorBoundaryRoute>
                <Suspense fallback={<PageLoader />}>
                  <Dashboard>
                    <Suspense fallback={<PageLoader />}>
                      <TastingListPage />
                    </Suspense>
                  </Dashboard>
                </Suspense>
              </ErrorBoundaryRoute>
            } />
            <Route path="/food-tasting/new" element={
              <ErrorBoundaryRoute>
                <Suspense fallback={<PageLoader />}>
                  <Dashboard>
                    <Suspense fallback={<PageLoader />}>
                      <TastingNewEventPage />
                    </Suspense>
                  </Dashboard>
                </Suspense>
              </ErrorBoundaryRoute>
            } />
            <Route path="/food-tasting/:eventId/review" element={
              <ErrorBoundaryRoute>
                <Suspense fallback={<PageLoader />}>
                  <Dashboard>
                    <Suspense fallback={<PageLoader />}>
                      <TastingReviewPage />
                    </Suspense>
                  </Dashboard>
                </Suspense>
              </ErrorBoundaryRoute>
            } />
            <Route path="/food-tasting/:eventId" element={
              <ErrorBoundaryRoute>
                <Suspense fallback={<PageLoader />}>
                  <Dashboard>
                    <Suspense fallback={<PageLoader />}>
                      <TastingCapturePage />
                    </Suspense>
                  </Dashboard>
                </Suspense>
              </ErrorBoundaryRoute>
            } />
            <Route path="/buffet-labels" element={
              <ErrorBoundaryRoute>
                <Suspense fallback={<PageLoader />}>
                  <Dashboard>
                    <Suspense fallback={<PageLoader />}>
                      <BuffetLabelPrintPage />
                    </Suspense>
                  </Dashboard>
                </Suspense>
              </ErrorBoundaryRoute>
            } />
            <Route path="/admin/users" element={
              <ErrorBoundaryRoute>
                <Suspense fallback={<PageLoader />}>
                  <Dashboard>
                    <Suspense fallback={<PageLoader />}>
                      <UsersAdmin />
                    </Suspense>
                  </Dashboard>
                </Suspense>
              </ErrorBoundaryRoute>
            } />
            <Route path="/admin/roles" element={
              <ErrorBoundaryRoute>
                <Suspense fallback={<PageLoader />}>
                  <Dashboard>
                    <Suspense fallback={<PageLoader />}>
                      <RolesAdmin />
                    </Suspense>
                  </Dashboard>
                </Suspense>
              </ErrorBoundaryRoute>
            } />
            <Route path="/admin/permissions" element={
              <ErrorBoundaryRoute>
                <Suspense fallback={<PageLoader />}>
                  <Dashboard>
                    <Suspense fallback={<PageLoader />}>
                      <PermissionsAdmin />
                    </Suspense>
                  </Dashboard>
                </Suspense>
              </ErrorBoundaryRoute>
            } />
            <Route path="/proposal-builder" element={
              <ErrorBoundaryRoute>
                <Suspense fallback={<PageLoader />}>
                  <Dashboard>
                    <Suspense fallback={<PageLoader />}>
                      <ProposalBuilder />
                    </Suspense>
                  </Dashboard>
                </Suspense>
              </ErrorBoundaryRoute>
            } />
            <Route path="/proposals" element={
              <ErrorBoundaryRoute>
                <Suspense fallback={<PageLoader />}>
                  <Dashboard>
                    <Suspense fallback={<PageLoader />}>
                      <ProposalsList />
                    </Suspense>
                  </Dashboard>
                </Suspense>
              </ErrorBoundaryRoute>
            } />
            <Route path="/document-test" element={
              <ErrorBoundaryRoute>
                <Suspense fallback={<PageLoader />}>
                  <Dashboard>
                    <Suspense fallback={<PageLoader />}>
                      <DocumentTestPage />
                    </Suspense>
                  </Dashboard>
                </Suspense>
              </ErrorBoundaryRoute>
            } />
            <Route path="/live-editor-test" element={
              <ErrorBoundaryRoute>
                <Suspense fallback={<PageLoader />}>
                  <LiveDocumentEditorTestPage />
                </Suspense>
              </ErrorBoundaryRoute>
            } />
            <Route path="/apryse-editor" element={
              <ErrorBoundaryRoute>
                <Suspense fallback={<PageLoader />}>
                  <ApryseDocumentEditorPage />
                </Suspense>
              </ErrorBoundaryRoute>
            } />
          </Route>
          <Route path="*" element={
            <ErrorBoundaryRoute>
              <Suspense fallback={<PageLoader />}>
                <NotFound />
              </Suspense>
            </ErrorBoundaryRoute>
          } />
        </Routes>
        </Suspense>
        <Toaster />
        <VercelAnalytics />
        <SpeedInsights />
      </div>
    </LoadingProvider>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AppRoutes />
    </BrowserRouter>
  );
}

export default App;
